import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Package, Loader2, AlertTriangle } from 'lucide-react';
import DateRangePicker from '../DateRangePicker';
import { InsightsAdmin, InsightsPortal } from '../logistics/Insights';
import { Insufficient, SectionTitle, fmtDate } from '../logistics/format';
import { ProductCards } from './ProductCards';
import { ViewsSection } from './ViewsSection';
import { AbcSection } from './AbcSection';
import { PairsSection } from './PairsSection';
import { StockSection } from './StockSection';

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const toRange = (f) => {
  if (f.mode === 'custom' && f.startDate && f.endDate) return { startDate: f.startDate, endDate: f.endDate };
  const end = new Date();
  const start = new Date(end.getFullYear(), end.getMonth(), end.getDate() - (Number(f.preset) - 1));
  return { startDate: ymd(start), endDate: ymd(end) };
};

const statusMessage = (r, isPortal) => {
  if (r.status === 'empty') return 'Sem pedidos pagos no período selecionado.';
  if (r.status === 'importing') return isPortal ? 'Estamos preparando os dados de produtos da sua loja.'
    : `Importando os itens dos pedidos desta loja: ${r.importing?.progress ?? 0}%. Os números aparecem assim que a importação terminar.`;
  return isPortal ? 'Análise indisponível para esta loja.'
    : `Só ${r.coverage_pct}% dos pedidos pagos do período têm os itens importados (mínimo de 70%). Esta análise não está disponível.`;
};
const VIEWS_MSG = {
  no_items: 'O GA4 desta loja não registra os produtos nos eventos de e-commerce.',
  unavailable: 'O GA4 não está configurado para este projeto (ou está em modo de demonstração).',
};
const STOCK_SUB = 'Produtos sem estoque que receberam visitas nos últimos 7 dias';
const STOCK_MSG = 'Adicione leitura de Produtos e Estoque ao token da Wake para ver esta análise.';

const Section = ({ title, sub, testId, children }) => (
  <section data-testid={testId}><SectionTitle sub={sub}>{title}</SectionTitle>{children}</section>
);

// Portal: stock comes from its own route; hidden when the store has no stock access.
const PortalStock = ({ api, range }) => {
  const [s, setS] = useState(null);
  useEffect(() => {
    let live = true;
    api.get('/estoque', { params: { start_date: range.startDate, end_date: range.endDate } })
      .then(({ data }) => live && setS(data)).catch(() => live && setS(null));
    return () => { live = false; };
  }, [api, range.startDate, range.endDate]);
  if (s?.status !== 'ok' || s.out_of_stock_sought_status !== 'ok') return null;
  return (
    <Section title="Sem estoque, mas procurados" testId="products-stock" sub={STOCK_SUB}>
      <StockSection s={s} isPortal />
    </Section>
  );
};

export const ProductsReport = ({ api, isPortal = false }) => {
  const [filter, setFilter] = useState({ mode: 'preset', preset: '30', startDate: null, endDate: null });
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const range = toRange(filter);
  const load = useCallback(async () => {
    setLoading(true);
    try { setR((await api.get('/report', { params: { start_date: range.startDate, end_date: range.endDate } })).data); }
    catch (e) { setR({ available: false, reason: 'Não foi possível carregar o relatório agora.' }); }
    finally { setLoading(false); }
  }, [api, range.startDate, range.endDate]);
  useEffect(() => { load(); }, [load]);
  const ok = r?.status === 'ok';
  const msg = r && statusMessage(r, isPortal);
  const blocked = (id) => <Insufficient message={msg} testId={`${id}-insufficient`} />;
  return (
    <div className="space-y-8" data-testid="products-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3"><Package className="h-7 w-7 text-indigo-600" /> Produtos</h1>
          <p className="text-sm text-muted-foreground mt-1">Itens de pedidos pagos, pela data do pedido. Comparação com o período anterior de mesma duração.</p>
        </div>
        <DateRangePicker mode={filter.mode} preset={filter.preset} startDate={filter.startDate} endDate={filter.endDate} onChange={(f) => setFilter((p) => ({ ...p, ...f }))} disabled={loading} />
      </div>
      {loading && <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>}
      {!loading && r && !r.available && <Card><CardContent className="py-12 text-center text-muted-foreground" data-testid="products-unavailable">{r.reason}</CardContent></Card>}
      {!loading && r?.available && (
        <>
          {r.partial && <Alert data-testid="products-partial-alert"><AlertTriangle className="h-4 w-4" /><AlertDescription>Dados parciais: os itens dos pedidos começam em {fmtDate(r.history_start_date)}.</AlertDescription></Alert>}
          {r.importing?.active && <Alert data-testid="products-importing-alert"><Loader2 className="h-4 w-4 animate-spin" /><AlertDescription>{statusMessage({ ...r, status: 'importing' }, isPortal)}</AlertDescription></Alert>}
          <p className="text-xs text-muted-foreground -mt-4" data-testid="products-period">
            {fmtDate(r.start_date)} a {fmtDate(r.end_date)} · {r.pedidos} pedidos pagos · comparado com {fmtDate(r.previous.start_date)} a {fmtDate(r.previous.end_date)}
          </p>
          <Section title="Visão geral" testId="products-overview" sub="Receita de produtos = total do pedido menos o frete, dividida entre os itens pelo valor de cada um.">
            {ok ? <ProductCards cards={r.cards} /> : blocked('products-overview')}
          </Section>
          {(r.views || !isPortal) && (
            <Section title="Vistos × vendidos" testId="products-views" sub={isPortal ? "Visitas e compras de cada produto na loja, no mesmo período." : "Dados do GA4 no mesmo período, agrupados por produto."}>
              {r.views?.status === 'ok' ? <ViewsSection v={r.views} api={api} isPortal={isPortal} />
                : <Insufficient message={VIEWS_MSG[r.views?.status] || r.views?.message || VIEWS_MSG.unavailable} testId="products-views-insufficient" />}
            </Section>
          )}
          <Section title="Curva ABC" testId="products-abc" sub="A: produtos que somam até 80% da receita · B: de 80% a 95% · C: o restante.">
            {ok ? <AbcSection abc={r.abc} isPortal={isPortal} /> : blocked('products-abc')}
          </Section>
          <Section title="Comprados juntos" testId="products-pairs" sub="Pares de produtos diferentes no mesmo pedido, ordenados pelo lift (quantas vezes mais que o acaso).">
            {ok ? <PairsSection pairs={r.pairs} isPortal={isPortal} /> : blocked('products-pairs')}
          </Section>
          {isPortal ? <PortalStock api={api} range={range} /> : (
            <Section title="Sem estoque, mas procurados" testId="products-stock" sub={STOCK_SUB}>
              {r.stock?.status !== 'ok' ? <Insufficient message={STOCK_MSG} testId="products-stock-insufficient" />
                : r.stock.out_of_stock_sought_status !== 'ok' ? <Insufficient message={VIEWS_MSG.no_items} testId="products-stock-no-ga4" />
                  : <StockSection s={r.stock} />}
            </Section>
          )}
          {isPortal ? <InsightsPortal api={api} /> : (
            <section>
              <SectionTitle sub="Geradas pela IA só com os números deste relatório. Edite antes de publicar.">Recomendações</SectionTitle>
              <InsightsAdmin api={api} range={range} />
            </section>
          )}
        </>
      )}
    </div>
  );
};
